
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import isEqual from 'lodash/isEqual';
import kebabCase from 'just-kebab-case';

function hasAnalyticsDirective(node) {
  if (!node) {
    return false;
  }

  return node.elm.nodeType === 1 && node.data.directives && node.data.directives.some(directive => directive.name === 'pendo-analytics');
}

function getType({
  value,
  arg
}) {
  const type = typeof value;

  if (type === 'string') {
    return arg;
  }

  if (value && type === 'object') {
    return value.type;
  }

  return false;
}
function getValue(value) {
  const type = typeof value;

  if (type === 'string') {
    return value;
  }

  if (value && type === 'object') {
    return value.value;
  }

  return false;
}
function applyAttributes(el, {
  value,
  type
} = {}) {
  if (type) {
    el.setAttribute('pendo-analytics', `${type}:${kebabCase(value)}`);
    return;
  }

  el.setAttribute('pendo-analytics', kebabCase(value));
}
function inserted(el, {
  value,
  arg
}, vnode) {
  if (vnode.isRootInsert && vnode.parent && hasAnalyticsDirective(vnode.parent)) {
    return;
  }

  const boundValue = getValue(value);
  const type = getType({
    value,
    arg
  });

  if (!boundValue) {
    return;
  }

  applyAttributes(el, {
    value: boundValue,
    type
  });
}
function update(el, {
  oldValue,
  value,
  arg
}, vnode) {
  if (vnode.isRootInsert && vnode.parent && hasAnalyticsDirective(vnode.parent)) {
    return;
  }

  if (isEqual(oldValue, value)) {
    return;
  }

  const boundValue = getValue(value);
  const type = getType({
    value,
    arg
  });

  if (!boundValue) {
    return;
  }

  applyAttributes(el, {
    value: boundValue,
    type
  });
}
function install(Vue) {
  if (install.installed) {
    return;
  }

  install.installed = true;
  Vue.directive('pendo-analytics', PendoAnalytics);
}
const PendoAnalytics = {
  inserted,
  update,
  install
};

function convertToUnit(str, unit = 'px') {
  if (str == null || str === '') {
    return undefined;
  }

  if (isNaN(str)) {
    return String(str);
  }

  return `${Number(str)}${unit}`;
}
function cached(fn) {
  const cache = Object.create(null);
  return function cachedFn(str) {
    const hit = cache[str];
    return hit || (cache[str] = fn(str));
  };
}
const capitalize = cached(str => {
  return str.charAt(0).toUpperCase() + str.slice(1);
});
function isWhitespace(node) {
  return node.isComment && !node.asyncFactory || node.text === ' ';
}
function hasSlot(vm, name = 'default', data = {}) {
  const scopedSlotFn = vm.$scopedSlots[name];
  let nodes;

  if (scopedSlotFn) {
    nodes = scopedSlotFn(data);
  } else {
    nodes = vm.$slots[name];
  }

  if (Array.isArray(nodes)) {
    return nodes.some(node => !isWhitespace(node));
  }

  return false;
}
const keyCodes = {
  esc: 27,
  end: 35,
  home: 36,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  enter: 13,
  space: 32
};

var labelsMixin = {
  directives: {
    PendoAnalytics
  },
  props: {
    label: {
      type: String,
      default: null
    },
    labelPosition: {
      type: String,
      default: 'top',
      validator: position => ['top', 'bottom', 'left', 'right'].includes(position)
    },
    labels: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    hasTopLabel() {
      return this.topLabel || hasSlot(this, 'topLabel');
    },

    hasBottomLabel() {
      return this.bottomLabel || hasSlot(this, 'bottomLabel');
    },

    topLabel() {
      if (this.label && ['top', 'left'].includes(this.labelPosition)) {
        return this.label;
      }

      return this.labels.top || this.labels.left;
    },

    bottomLabel() {
      if (this.label && ['bottom', 'right'].includes(this.labelPosition)) {
        return this.label;
      }

      return this.labels.bottom || this.labels.right;
    },

    inlineLabel() {
      return ['left', 'right'].includes(this.labelPosition) || this.labels.left || this.labels.right;
    },

    suffixLabel() {
      if (this.label && this.labelPosition === 'suffix') {
        return this.label;
      }

      return this.labels.suffix;
    },

    analyticsTagFromLabels() {
      const tag = [];

      if (this.topLabel) {
        tag.push(this.topLabel);
      }

      if (this.bottomLabel) {
        tag.push(this.bottomLabel);
      }

      if (this.inlineLabel) {
        tag.push(this.inlineLabel);
      }

      return tag.join(' ');
    }

  }
};

export { PendoAnalytics as P, getValue as a, applyAttributes as b, install as c, PendoAnalytics as d, capitalize as e, convertToUnit as f, getType as g, hasSlot as h, inserted as i, cached as j, keyCodes as k, labelsMixin as l, update as u };
